import React, { useState, useEffect, useMemo } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as Shared from "./Shared.js";
import SignatureModal from "./SignatureModal.js";

/*
 * Reviewer workspace: one card per section, previously-approved vs. new, approve
 * (-> SignatureModal) or reject with a comment. Footer signs the whole document once
 * every section is approved.
 */

const API = "/umbraco/api/TechnicalDocumentation";
const SV = { DRAFT: 0, APPROVED: 1, REJECTED: 2 };
const KIND = { 0: null, 1: "Added", 2: "Moved", 3: "Deleted" };

const ApprovalPane = ({ techDocId, versionId, versionNumber, documentName, signer, onClose, onChanged }) => {
    const [items, setItems] = useState([]);
    const [allApproved, setAllApproved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");        // all | drafts
    const [rejectFor, setRejectFor] = useState(null);    // sectionVersionId
    const [rejectText, setRejectText] = useState("");
    const [signing, setSigning] = useState(null);        // { mode:"section"|"doc", item? }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { load(); }, [versionId]);

    const load = () => {
        setLoading(true);
        fetch(`${API}/ReviewQueue/?techDocId=${techDocId}&versionId=${versionId}`)
            .then((r) => r.json())
            .then((d) => { setItems(d?.items || []); setAllApproved(!!d?.allApproved); })
            .catch(() => setError("Could not load the review queue."))
            .finally(() => setLoading(false));
    };

    const counts = useMemo(() => {
        const c = { approved: 0, pending: 0, rejected: 0 };
        items.forEach((i) => {
            const s = i.Current?.State;
            if (s === SV.APPROVED) c.approved++;
            else if (s === SV.REJECTED) c.rejected++;
            else c.pending++;
        });
        return c;
    }, [items]);

    const shown = items.filter((i) => {
        if (filter === "drafts") return (i.Current?.State ?? SV.DRAFT) === SV.DRAFT;
        return true;
    });

    const doReject = (item) => {
        if (!rejectText.trim()) return;
        setBusy(true);
        fetch(`${API}/RejectSectionVersion/`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ TechDocId: techDocId, SectionVersionId: item.Current.Id, Comment: rejectText.trim() }),
        })
            .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
            .then(() => { setRejectFor(null); setRejectText(""); load(); onChanged && onChanged(); })
            .catch(() => setError("Could not reject the section."))
            .finally(() => setBusy(false));
    };

    const doSign = (username, password) => {
        setBusy(true);
        setError(null);
        const isDoc = signing.mode === "doc";
        const url = isDoc ? `${API}/ApproveAndSign/` : `${API}/ApproveSectionVersion/`;
        const body = isDoc
            ? { TechDocId: techDocId, Username: username, Password: password }
            : { TechDocId: techDocId, SectionVersionId: signing.item.Current.Id, Username: username, Password: password };
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
            .then((r) => r.json())
            .then((d) => {
                if (d?.success === false) { setError(d.message || "Incorrect username or password. Please try again."); return; }
                setSigning(null);
                onChanged && onChanged();
                if (isDoc) { onClose(); return; }
                load();
            })
            .catch(() => setError("Could not sign."))
            .finally(() => setBusy(false));
    };

    return (
        <div className="td-review-overlay">
            <div className="td-pane-header">
                <div className="td-pane-header-left">
                    <i className="fas fa-clipboard-check" style={{ color: "#2b6cb0" }} />
                    <span className="td-pane-title">{documentName} — Review v{versionNumber}</span>
                </div>
                <div className="td-pane-header-right">
                    <span className="td-muted" style={{ fontSize: 12 }}>
                        {counts.approved} approved · {counts.pending} pending · {counts.rejected} rejected
                    </span>
                    <Shared.Avatar name={signer?.name} id={signer?.id} />
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
            </div>

            {error && (
                <div className="td-err" style={{ padding: "8px 28px", background: "#fde8ec", flex: "none" }}>
                    {error} <span style={{ cursor: "pointer", marginLeft: 8, textDecoration: "underline" }} onClick={() => setError(null)}>dismiss</span>
                </div>
            )}

            <div className="td-review-body">
                <div className="td-review-rail">
                    <div className="td-review-filters">
                        {[["all", `All ${items.length}`], ["drafts", `Drafts ${counts.pending}`]].map(([k, label]) => (
                            <span key={k} className={`td-fchip ${filter === k ? "on" : ""}`} onClick={() => setFilter(k)}>{label}</span>
                        ))}
                    </div>
                    <div style={{ overflowY: "auto", flex: 1 }}>
                        {shown.map((i) => (
                            <a key={i.SectionId} href={`#sec-${i.SectionId}`} className="td-rnode" style={{ textDecoration: "none" }}>
                                <Shared.SectionVersionDot state={i.Current?.State ?? 0} />
                                <span style={{ flex: 1, fontSize: 12.5 }}>{i.Number} {i.Title}</span>
                                {i.Current && <span className="td-vmini">v{i.Current.Major}.{i.Current.Minor}</span>}
                                {i.ChangeKind !== 0 && <span className="td-chip-structural">{KIND[i.ChangeKind]}</span>}
                            </a>
                        ))}
                    </div>
                </div>

                <div className="td-review-center">
                    {loading ? (
                        <div className="td-center-loading"><LoadingSpinnerImage /></div>
                    ) : (
                        <>
                            <div className="td-aicard" style={{ marginBottom: 14 }}>
                                <i className="fas fa-robot" />
                                <div><b>AI Review Summary.</b> An AI-written summary of what changed across the round will appear here. <b>Available soon.</b></div>
                            </div>

                            {shown.length === 0 && <div className="td-empty"><div className="td-empty-card">Nothing in this filter.</div></div>}

                            {shown.map((item) => {
                                const sv = item.Current;
                                const decided = sv?.State === SV.APPROVED || sv?.State === SV.REJECTED;
                                return (
                                    <div key={item.SectionId} id={`sec-${item.SectionId}`} className="td-structural-card">
                                        <div className="td-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                                            <div>
                                                <b>{item.Number} {item.Title}</b>{" "}
                                                {item.ChangeKind !== 0 && <span className="td-chip-structural">{KIND[item.ChangeKind]}</span>}
                                                <div className="td-muted" style={{ fontSize: 11.5 }}>
                                                    v{sv?.Major}.{sv?.Minor} · changed by {item.ChangedByName || "—"} · {Shared.readableAge(sv?.UpdatedOn)}
                                                </div>
                                            </div>
                                            <div className="td-row" style={{ gap: 8 }}>
                                                <Shared.SectionVersionPill state={sv?.State ?? 0} />
                                            </div>
                                        </div>

                                        <div className="td-diffcols">
                                            {item.PreviousApproved && (
                                                <div className="td-diffcol">
                                                    <div className="td-diffhead">Previously approved · v{item.PreviousApproved.Major}.{item.PreviousApproved.Minor}</div>
                                                    <Shared.SafeHTML className="td-readonly-body td-diff-hide-added" html={item.DiffHtml || "<p><em>empty</em></p>"} />
                                                </div>
                                            )}
                                            <div className="td-diffcol">
                                                <div className="td-diffhead">{item.PreviousApproved ? "New · " : ""}v{sv?.Major}.{sv?.Minor}{item.PreviousApproved ? " (pending)" : ""}</div>
                                                <Shared.SafeHTML
                                                    className={`td-readonly-body${item.PreviousApproved ? " td-diff-hide-deleted" : ""}`}
                                                    html={(item.PreviousApproved ? item.DiffHtml : sv?.ContentText) || "<p><em>empty</em></p>"}
                                                />
                                            </div>
                                        </div>

                                        {sv?.State === SV.REJECTED && sv?.ReviewComment && (
                                            <div className="td-banner" style={{ background: "#fde8ec", color: "#a3132f", marginTop: 8 }}>
                                                <span><i className="fas fa-times-circle" /> Rejected: {sv.ReviewComment}</span>
                                            </div>
                                        )}
                                        {sv?.State === SV.APPROVED && (
                                            <div className="td-receipt" style={{ marginTop: 8 }}>
                                                <i className="fas fa-check-circle" style={{ color: "#1f7a52" }} /> Approved &amp; signed
                                                {item.Signature?.CreatedByEmail ? ` by ${item.Signature.CreatedByEmail}` : ""} · v{sv.Major}.{sv.Minor}
                                            </div>
                                        )}

                                        {!decided && rejectFor === sv.Id && (
                                            <div style={{ marginTop: 8 }}>
                                                <textarea
                                                    className="td-commentbox" autoFocus rows={2}
                                                    placeholder="Why is this section rejected? (required)"
                                                    value={rejectText} onChange={(e) => setRejectText(e.target.value)}
                                                />
                                                <div className="td-row" style={{ gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
                                                    <button className="td-btn td-btn-cancel" onClick={() => { setRejectFor(null); setRejectText(""); }}>Cancel</button>
                                                    <button className="td-btn td-btn-cancel" style={{ color: "#a3132f", borderColor: "#a3132f" }}
                                                        disabled={busy || !rejectText.trim()} onClick={() => doReject(item)}
                                                        title={!rejectText.trim() ? "Enter a reason for rejecting this section." : undefined}>
                                                        Confirm reject
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {!decided && rejectFor !== sv.Id && (
                                            <div className="td-review-actionbar">
                                                <button className="td-btn td-btn-outline" onClick={() => { setRejectFor(sv.Id); setRejectText(""); }}>
                                                    <i className="fas fa-times" /> Reject section
                                                </button>
                                                <button className="td-btn td-btn-approve" onClick={() => setSigning({ mode: "section", item })}>
                                                    <i className="fas fa-check" /> Approve &amp; sign section
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </div>

            <div className="td-review-footer">
                <span className="td-muted" style={{ fontSize: 12.5 }}>
                    {counts.pending > 0
                        ? `${counts.pending} section${counts.pending > 1 ? "s" : ""} still need a decision`
                        : "Every section is approved."}
                </span>
                <button
                    className="td-btn td-btn-approve"
                    disabled={!allApproved || busy}
                    onClick={() => setSigning({ mode: "doc" })}
                    title={!allApproved
                        ? `Blocked: ${counts.pending} section${counts.pending > 1 ? "s" : ""} still need${counts.pending === 1 ? "s" : ""} a decision before the whole document can be signed.`
                        : undefined}
                >
                    <i className="fas fa-signature" /> Approve &amp; sign document
                </button>
            </div>

            {signing && (
                <SignatureModal
                    title={signing.mode === "doc"
                        ? `Electronic signature — approve document v${versionNumber}`
                        : `Electronic signature — approve ${signing.item.Number} ${signing.item.Title}`}
                    statement={signing.mode === "doc"
                        ? `By signing below, I confirm that I have reviewed all ${items.length} section(s) of "${documentName}" and approve version ${versionNumber} for release.`
                        : `By signing below, I confirm that I have reviewed section ${signing.item.Number} "${signing.item.Title}" and approve it.`}
                    compareContent={signing.mode === "doc" ? (
                        <div>
                            {items.map((i) => (
                                <div key={i.SectionId} className="td-kv" style={{ fontSize: 12 }}>
                                    <span>{i.Number} {i.Title}{i.ChangeKind !== 0 ? ` — ${KIND[i.ChangeKind]}` : ""}</span>
                                    <span><Shared.SectionVersionPill state={i.Current?.State ?? 0} /> v{i.Current?.Major}.{i.Current?.Minor}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    signerName={signer?.name}
                    signerRole={signer?.role}
                    signerEmail={signer?.email}
                    busy={busy}
                    error={error}
                    onSign={doSign}
                    onClose={() => { setSigning(null); setError(null); }}
                />
            )}
        </div>
    );
};

export default ApprovalPane;
